/**
 * The My Tasks views' rules (z8nrz7fh6h): which views exist, the month grid,
 * how full a day is and the colour that says so, the agenda's order, and the
 * summary a planner reads first. Pure, dates as `YYYY-MM-DD` through UTC parts.
 */

import type {
  ActivityCode,
  CalendarActivity,
  CalendarResource,
  DayLoad,
} from '@/api/resourceCalendarApi'
import { isOverdue, isoAddDays } from './myTasks'

export type CalendarViewId = 'board' | 'agenda' | 'workload' | 'month'

export const CALENDAR_VIEWS: { id: CalendarViewId; label: string; hint: string }[] = [
  { id: 'board', label: 'Board', hint: 'People by day — drag work between them' },
  { id: 'agenda', label: 'Agenda', hint: 'Everything in order, day by day' },
  { id: 'workload', label: 'Workload', hint: 'Who is free and who is over capacity' },
  { id: 'month', label: 'Month', hint: 'Six weeks at a glance' },
]

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const parts = (date: string) => date.split('-').map(Number) as [number, number, number]
const pad = (n: number) => String(n).padStart(2, '0')

/** The first of the month `n` months from the one `date` is in. */
export function addMonths(date: string, n: number): string {
  const [y, m] = parts(date)
  const t = new Date(Date.UTC(y, m - 1 + n, 1))
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-01`
}

/** A month as six Monday-first weeks: always 42 days, so rows never jump. */
export function monthGrid(anchor: string): {
  label: string
  first: string
  from: string
  to: string
  weeks: string[][]
} {
  const first = addMonths(anchor, 0)
  const [y, m] = parts(first)
  const dow = new Date(Date.UTC(y, m - 1, 1)).getUTCDay()
  const from = isoAddDays(first, -((dow + 6) % 7))
  const days = Array.from({ length: 42 }, (_, i) => isoAddDays(from, i))
  return {
    label: `${MONTHS[m - 1]} ${y}`,
    first,
    from,
    to: days[41],
    weeks: Array.from({ length: 6 }, (_, w) => days.slice(w * 7, w * 7 + 7)),
  }
}

/** Planned over available, in %; null on a day with no shift and no work. */
export function utilization(load: DayLoad): number | null {
  if (load.availableHrs <= 0) return load.assignedHrs > 0 ? Infinity : null
  return Math.round((load.assignedHrs / load.availableHrs) * 100)
}

/** Calm to hot as a day fills; past full is the one loud colour. Literal classes. */
export function heatClass(pct: number | null): string {
  if (pct === null) return 'bg-muted/40 text-muted-foreground'
  if (pct > 100) return 'bg-destructive/85 text-white'
  if (pct >= 85) return 'bg-amber-400/80 text-amber-950'
  if (pct >= 60) return 'bg-primary/45 text-primary-foreground'
  if (pct >= 30) return 'bg-primary/25 text-foreground'
  if (pct > 0) return 'bg-primary/10 text-foreground'
  return 'bg-card text-muted-foreground'
}

const isOpen = (a: CalendarActivity) => a.state === 'PLANNED' || a.state === 'IN_PROGRESS'

/** One group per day of the window, timed work first by its time, then by type. */
export function agendaGroups(
  acts: CalendarActivity[],
  days: string[],
): { date: string; items: CalendarActivity[] }[] {
  return days.map((date) => ({
    date,
    items: acts
      .filter((a) => a.date === date)
      .sort(
        (a, b) =>
          (a.startTime ?? '99:99').localeCompare(b.startTime ?? '99:99') ||
          a.type.localeCompare(b.type) ||
          a.description.localeCompare(b.description),
      ),
  }))
}

/** The four numbers a planner reads before anything else. */
export function summaryStats(
  resources: CalendarResource[],
  acts: CalendarActivity[],
  today: string,
): { plannedHrs: number; unassigned: number; overloadedPeople: number; overdue: number } {
  const open = acts.filter(isOpen)
  const planned = open.filter((a) => a.ownerId !== null && a.type !== 'UA')
  return {
    plannedHrs: Math.round(planned.reduce((s, a) => s + a.hours, 0) * 100) / 100,
    unassigned: open.filter((a) => a.ownerId === null).length,
    overloadedPeople: resources.filter((r) => r.days.some((d) => d.overloaded)).length,
    overdue: acts.filter((a) => isOverdue(a, today)).length,
  }
}

/** One month tile: how much, of which types, and what needs attention. */
export function monthDaySummary(
  acts: CalendarActivity[],
  date: string,
  today: string,
): { count: number; types: ActivityCode[]; plannedHrs: number; unassigned: number; overdue: number } {
  const day = acts.filter((a) => a.date === date)
  const types: ActivityCode[] = []
  for (const a of day) if (!types.includes(a.type)) types.push(a.type)
  const open = day.filter(isOpen)
  return {
    count: day.length,
    types,
    plannedHrs:
      Math.round(
        open.filter((a) => a.ownerId !== null && a.type !== 'UA').reduce((s, a) => s + a.hours, 0) * 100,
      ) / 100,
    unassigned: open.filter((a) => a.ownerId === null).length,
    overdue: day.filter((a) => isOverdue(a, today)).length,
  }
}

export type QuickFilter = 'unassigned' | 'overdue' | null

/** The summary chips' one-click narrowing. */
export function quickFilter(acts: CalendarActivity[], quick: QuickFilter, today: string): CalendarActivity[] {
  if (quick === 'unassigned') return acts.filter((a) => a.ownerId === null && isOpen(a))
  if (quick === 'overdue') return acts.filter((a) => isOverdue(a, today))
  return acts
}
